<template>
  <v-container>
    <div class="d-flex align-center mb-4">
      <v-btn :to="{ name: 'TournamentResultsList' }" variant="outlined" class="mr-3">Back to results</v-btn>
      <h1>Edit tournament results</h1>
    </div>

    <v-card v-if="loading" class="pa-4 text-center">Loading…</v-card>
    <v-card v-else-if="!result" class="pa-4 text-center">Result not found.</v-card>
    <v-card v-else>
      <v-card-title class="text-body-1">
        {{ result.tournamentName }} — {{ formatDate(result.endedAt) }}
      </v-card-title>
      <v-card-subtitle>
        Buy-in: ${{ result.buyInAmount != null ? Number(result.buyInAmount).toLocaleString() : '—' }}
        · Prize pool: ${{ result.totalPrizePool != null ? Number(result.totalPrizePool).toLocaleString() : '—' }}
      </v-card-subtitle>
      <v-card-text>
        <v-select
          v-model="editedLeagueId"
          :items="[{ title: 'No league', value: null }, ...availableLeagues.map(l => ({ title: l.name, value: l.id }))]"
          label="League"
          hide-details
          clearable
          class="mb-4"
          style="max-width: 320px;"
        />
        <p class="text-body-2 mb-3">Winning positions (place, player name, amount won):</p>
        <v-table density="compact">
          <thead>
            <tr>
              <th>Place</th>
              <th>Player name</th>
              <th v-if="hasRebuys">Rebuys</th>
              <th v-if="hasAddons">Add-on</th>
              <th>Winnings ($)</th>
              <th>Paid ($)</th>
              <th>Knocked out by</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in editedPlayers" :key="index">
              <td>
                <v-text-field
                  v-model.number="row.place"
                  type="number"
                  min="1"
                  hide-details
                  density="compact"
                  style="max-width: 80px;"
                />
              </td>
              <td>
                <v-text-field
                  v-model="row.name"
                  hide-details
                  density="compact"
                  placeholder="Player name"
                />
              </td>
              <td v-if="hasRebuys">
                <v-text-field
                  v-model.number="row.rebuys"
                  type="number"
                  min="0"
                  hide-details
                  density="compact"
                  style="max-width: 80px;"
                  @update:model-value="recalcTotalPaid(row)"
                />
              </td>
              <td v-if="hasAddons">
                <v-checkbox
                  v-model="row.addOn"
                  hide-details
                  density="compact"
                  @update:model-value="recalcTotalPaid(row)"
                />
              </td>
              <td>
                <v-text-field
                  v-model.number="row.winnings"
                  type="number"
                  min="0"
                  step="0.01"
                  hide-details
                  density="compact"
                  placeholder="0"
                  style="max-width: 120px;"
                />
              </td>
              <td class="text-no-wrap">${{ Number(row.totalPaid).toLocaleString() }}</td>
              <td>
                <v-text-field
                  v-model="row.knockedOutBy"
                  hide-details
                  density="compact"
                  placeholder="—"
                  style="max-width: 140px;"
                />
              </td>
              <td>
                <v-btn icon size="small" color="error" variant="text" @click="removeRow(index)" title="Remove row">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
        <v-btn color="primary" variant="outlined" class="mt-3" @click="addRow" prepend-icon="mdi-plus">
          Add position
        </v-btn>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" @click="save" :loading="saving">Save results</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { db } from '@/firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { SAVED_TOURNAMENT_RESULTS_DB } from '@/constants/constants';
import { useUserStore } from '@/stores/users';
import { getLeaguesForRunner } from '@/api/leagues';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const loading = ref(true);
const saving = ref(false);
const result = ref(null);
const editedPlayers = ref([]);
const availableLeagues = ref([]);
const editedLeagueId = ref(null);

const resultId = computed(() => route.params.id);
const userId = computed(() => userStore.userid || userStore.user?.user_id);
const hasRebuys = computed(() => (result.value?.rebuyAmount || 0) > 0);
const hasAddons = computed(() => (result.value?.addonAmount || 0) > 0);

function sortByPlace() {
  editedPlayers.value.sort((a, b) => (Number(a.place) || 0) - (Number(b.place) || 0));
}

function formatDate(ms) {
  if (ms == null) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function addRow() {
  const places = editedPlayers.value.map(p => p.place).filter(n => typeof n === 'number' && n >= 1);
  const nextPlace = places.length > 0 ? Math.max(...places) + 1 : editedPlayers.value.length + 1;
  editedPlayers.value.push({
    place: nextPlace,
    name: '',
    winnings: 0,
    totalPaid: 0,
    durationSeconds: 0,
    knockedOutBy: '',
    rebuys: 0,
    addOn: false
  });
  sortByPlace();
}

function removeRow(index) {
  editedPlayers.value.splice(index, 1);
}

function recalcTotalPaid(row) {
  const buyin = Number(result.value?.buyInAmount) || 0;
  const rebuyAmt = Number(result.value?.rebuyAmount) || 0;
  const addonAmt = Number(result.value?.addonAmount) || 0;
  row.totalPaid = buyin + (row.rebuys || 0) * rebuyAmt + (row.addOn ? addonAmt : 0);
}

async function loadResult() {
  const id = resultId.value;
  if (!id) {
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    const [snap, leagues] = await Promise.all([
      getDoc(doc(db, SAVED_TOURNAMENT_RESULTS_DB, id)),
      getLeaguesForRunner(userId.value, userStore.username),
    ]);
    availableLeagues.value = leagues;

    if (!snap.exists()) {
      result.value = null;
      editedPlayers.value = [];
    } else {
      const data = snap.data();
      if (data.createdBy !== userId.value) {
        result.value = null;
        editedPlayers.value = [];
      } else {
        result.value = { id: snap.id, ...data };
        editedLeagueId.value = data.leagueId || null;
        const players = data.players || [];
        editedPlayers.value = players.length > 0
          ? players.map(p => ({
              place: p.place,
              name: p.name ?? '',
              winnings: p.winnings ?? 0,
              totalPaid: p.totalPaid ?? 0,
              durationSeconds: p.durationSeconds ?? 0,
              knockedOutBy: p.knockedOutBy ?? '',
              rebuys: p.rebuys ?? 0,
              addOn: p.addOn || false,
              linkedUserAlias: p.linkedUserAlias,
              linkedUserId: p.linkedUserId
            }))
          : [{ place: 1, name: '', winnings: 0, totalPaid: 0, durationSeconds: 0, knockedOutBy: '', rebuys: 0, addOn: false }];
        sortByPlace();
      }
    }
  } catch (e) {
    console.error('Error loading result:', e);
    result.value = null;
    editedPlayers.value = [];
  }
  loading.value = false;
}

async function save() {
  if (!result.value || !resultId.value) return;
  saving.value = true;
  try {
    const players = editedPlayers.value.map(p => {
      const base = {
        place: Number(p.place) || 1,
        name: String(p.name ?? '').trim(),
        winnings: Number(p.winnings) ?? 0,
        totalPaid: Number(p.totalPaid) ?? 0,
        durationSeconds: Number(p.durationSeconds) ?? 0,
        knockedOutBy: String(p.knockedOutBy ?? '').trim(),
        rebuys: p.rebuys ?? 0,
        addOn: p.addOn || false
      };
      if (p.linkedUserAlias) base.linkedUserAlias = p.linkedUserAlias;
      if (p.linkedUserId) base.linkedUserId = p.linkedUserId;
      return base;
    });
    const docRef = doc(db, SAVED_TOURNAMENT_RESULTS_DB, result.value.id);
    const payload = {
      tournamentName: result.value.tournamentName,
      createdBy: result.value.createdBy,
      endedAt: result.value.endedAt,
      buyInAmount: result.value.buyInAmount,
      rebuyAmount: result.value.rebuyAmount,
      addonAmount: result.value.addonAmount,
      totalPrizePool: result.value.totalPrizePool,
      levels: result.value.levels || [],
      payouts: result.value.payouts || [],
      leagueId: editedLeagueId.value || null,
      players
    };
    await setDoc(docRef, payload, { merge: true });
    result.value = { ...result.value, players, leagueId: editedLeagueId.value || null };
    router.push({ name: 'TournamentResultsList' });
  } catch (e) {
    console.error('Error saving result:', e);
  }
  saving.value = false;
}

watch(resultId, () => loadResult(), { immediate: false });
onMounted(loadResult);
</script>
